import React from 'react';
import {
  ModalOverlay,
  ModalOverlayProps,
} from 'components/common/modal-overlay';
import styled from 'styled-components';
import {
  FilterContext,
  FilterSection,
} from 'components/common/search/sidebar/filters';
import { CEB_COLOR, CEB_COLOR_RGBA } from 'app-constants/CEB_COLOR';
import { SearchContext } from 'components/layouts/research-layout/SearchContext';
import pickyBy from 'lodash/fp/pickBy';
import { JsonForm } from 'components/common/json_form';
import { StyledFormInput } from 'styles/form';
import pluralize from 'util/pluralize';
import { SelfGuttingScroll } from './SelfGuttingScroll';

export interface TitleFilterModalProps {
  modalVisible?: boolean;
  closeModal: (
    event: React.SyntheticEvent<HTMLElement, Event> | undefined,
    selected: any,
  ) => void;
}

export function TitleFilterModal({
  modalVisible,
  closeModal,
}: TitleFilterModalProps) {
  return (
    <ModalOverlay
      maxHeight="526px"
      minHeight="526px"
      maxWidth="518px"
      contentHeight="auto"
      visible={modalVisible}
      closeModal={
        closeModal as (event?: React.SyntheticEvent<HTMLElement, Event>) => void
      }
    >
      <StyledTitleFilterModalContent>
        <FilterSelection closeModal={closeModal} />
      </StyledTitleFilterModalContent>
    </ModalOverlay>
  );
}

interface FilterSelectionProps
  extends Pick<TitleFilterModalProps, 'closeModal'> {}

function FilterSelection({ closeModal }: FilterSelectionProps) {
  const { changeOptionFilters } = React.useContext(SearchContext);
  const { sections, optionValues } = React.useContext(FilterContext)!;
  const [filterText, setFilterText] = React.useState(undefined);
  const applyOptions = React.useCallback(
    ({ filterText: _, ...values }: Record<string, any>) => {
      const valueChecked = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, v === 'on']),
      );

      const filters = pickyBy((_, k) => {
        return k.startsWith('title_') ? valueChecked[k] : true;
      })(optionValues);

      changeOptionFilters({ ...filters, ...valueChecked });
      setFilterText(undefined);
      closeModal(undefined, valueChecked);
    },
    [closeModal, changeOptionFilters, optionValues],
  );

  const filterOptions = React.useCallback(
    (form: Record<string, any>) => setFilterText(form.filterText),
    [],
  );

  const total = React.useMemo(
    () =>
      sections?.publicationTitles?.children?.filter(o => o && o.count! > 0)
        .length || 0,
    [sections],
  );

  return (
    <StyledTitleFilterForm onChange={filterOptions} onSubmit={applyOptions}>
      <TitleFilterModalHeader
        closeModal={
          closeModal as (
            event?: React.SyntheticEvent<HTMLElement, Event>,
          ) => void
        }
        title={`${total} ${pluralize(total, 'Title')} match${
          total === 1 ? 'es' : ''
        } your Search`}
      />
      <SelfGuttingScroll>
        <StyledTitleFilterText
          placeholder="Start typing to filter the list below..."
          name="filterText"
          autoComplete="off"
        />

        <FilterSection
          name="Titles"
          section="publicationTitles"
          noEmpty
          filterText={filterText}
          expandable={false}
        />
      </SelfGuttingScroll>
    </StyledTitleFilterForm>
  );
}

const StyledTitleFilterText = styled(StyledFormInput)`
  > input[type='text'] {
    border-radius: 3px;
    border: solid 1px #dededb;
    box-shadow: none;
    color: ${CEB_COLOR_RGBA('BLACK', 0.6)};
    font-family: 'Zilla Slab', serif;
    font-size: 15px;
    font-stretch: normal;
    font-style: normal;
    font-weight: normal;
    padding: 6px 12px;
    &::placeholder {
      color: ${CEB_COLOR_RGBA('BLACK', 0.6)};
    }
  }
`;

interface TitleFilterModalHeaderProps
  extends Pick<ModalOverlayProps, 'closeModal'> {
  title: string;
}

function TitleFilterModalHeader({
  closeModal,
  title,
}: TitleFilterModalHeaderProps) {
  return (
    <StyledModalHeader>
      <section>
        <h3>{title}</h3>
      </section>
      <section>
        <TitleFormHeaderButton onClick={closeModal}>
          Cancel
        </TitleFormHeaderButton>
        <TitleFormHeaderButton type="submit">Apply</TitleFormHeaderButton>
      </section>
    </StyledModalHeader>
  );
}

const TitleFormHeaderButton = styled.button`
  border-radius: 3px;
  color: ${CEB_COLOR('NICE_BLUE')};
  font-family: 'BasisGrotesque', sans-serif;
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  padding: 0;
  text-align: center;

  &[type='submit'] {
    background: ${CEB_COLOR('NICE_BLUE')};
    color: white;
    font-size: 17px;
    font-weight: normal;
    padding: 9px 15px 8px 17px;
  }
  + button {
    margin-left: 18px;
  }
`;

export const StyledTitleFilterModalContent = styled.section`
  box-sizing: border-box;
  height: 100%;
  padding: 32px 36px 0 32px;
  > form > section {
    overflow: auto;
  }
`;

const StyledTitleFilterForm = styled(JsonForm)`
  height: 100%;
  display: flex;
  flex-direction: column;
  label {
    padding: 4px 0;
    font-size: 15px;
  }
`;

const StyledModalHeader = styled.header`
  margin-bottom: 18px;
  display: flex;
  h3 {
    font-family: 'Zilla Slab', serif;
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
  }
  section {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    &:last-child {
      flex: 0 1 auto;
    }
    > button.close {
      float: right;
      font-size: 20px;
      color: ${CEB_COLOR('BLACK')};
    }
  }
`;
